import { useState, useEffect } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ArrowLeft, CheckCircle2 } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [token, setToken] = useState(params.get("token") || "");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    const t = params.get("token");
    if (t) setToken(t);
  }, [params]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
      setTimeout(() => navigate("/login"), 1800);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6" data-testid="reset-password-page">
      <div className="w-full max-w-[420px]">
        <Link to="/login" className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-8">
          <ArrowLeft className="h-3 w-3" /> Back to sign in
        </Link>

        <h2 className="font-display font-extrabold tracking-tight text-3xl">
          Choose a new password
        </h2>
        <p className="text-sm text-muted-foreground mt-2 mb-6">
          Tokens expire after one hour. Use 8 or more characters.
        </p>

        {done ? (
          <div className="border border-[hsl(var(--success))] bg-[hsl(var(--success))/0.05] rounded-md p-4 flex gap-3 items-start" data-testid="reset-success">
            <CheckCircle2 className="h-5 w-5 text-[hsl(var(--success))] shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold text-sm">Password updated</div>
              <div className="text-xs text-muted-foreground">Redirecting to sign in…</div>
            </div>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="space-y-5">
            <div className="space-y-1.5">
              <Label htmlFor="token" className="text-xs font-semibold uppercase tracking-wider">Reset Token</Label>
              <Input
                id="token"
                required
                value={token}
                onChange={(e) => setToken(e.target.value)}
                placeholder="Paste your token"
                data-testid="reset-token-input"
                className="h-11 font-mono text-xs"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-xs font-semibold uppercase tracking-wider">New Password</Label>
              <Input
                id="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="reset-new-password-input"
                className="h-11"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="confirm" className="text-xs font-semibold uppercase tracking-wider">Confirm</Label>
              <Input
                id="confirm"
                type="password"
                required
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                data-testid="reset-confirm-password-input"
                className="h-11"
              />
            </div>
            {error && (
              <div className="text-sm text-destructive border border-destructive/30 bg-destructive/5 rounded-md px-3 py-2" data-testid="reset-error">
                {error}
              </div>
            )}
            <Button type="submit" disabled={loading} className="w-full h-11" data-testid="reset-submit-button">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update password"}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
